import type {
  ErpCustomerBackfillField,
  ErpCustomerBackfillFieldComparison,
} from '@/contracts/customers'
import type { ManagedCustomer } from '@/lib/business-workflow'

type FieldKind = 'text' | 'number'

type ErpCustomerBackfillFieldDefinition = {
  field: ErpCustomerBackfillField
  label: string
  kind: FieldKind
}

export type ErpCustomerBackfillSource = Partial<
  Record<ErpCustomerBackfillField, string | number | null | undefined>
> &
  Record<string, unknown>

export const erpCustomerBackfillFieldDefinitions: ErpCustomerBackfillFieldDefinition[] = [
  { field: 'erpCustomerCode', label: '客戶代號', kind: 'text' },
  { field: 'companyName', label: '客戶簡稱', kind: 'text' },
  { field: 'taxId', label: '統一編號', kind: 'text' },
  { field: 'industry', label: '通路別', kind: 'text' },
  { field: 'companySize', label: '公司規模', kind: 'text' },
  { field: 'website', label: '網站', kind: 'text' },
  { field: 'companyAddress', label: '公司地址', kind: 'text' },
  { field: 'companyPhone', label: '公司電話', kind: 'text' },
  { field: 'contactName', label: '主要聯絡人', kind: 'text' },
  { field: 'contactTitle', label: '聯絡人職稱', kind: 'text' },
  { field: 'contactEmail', label: '聯絡人 Email', kind: 'text' },
  { field: 'contactPhone', label: '聯絡人電話', kind: 'text' },
  { field: 'decisionRole', label: '決策角色', kind: 'text' },
  { field: 'paymentTerms', label: '付款條件', kind: 'text' },
  { field: 'creditLimit', label: '授信額度', kind: 'number' },
  { field: 'salesRegion', label: '業務區域', kind: 'text' },
  { field: 'expectedValue', label: '預估成交金額', kind: 'number' },
  { field: 'needSummary', label: '需求摘要', kind: 'text' },
  { field: 'internalNotes', label: '內部備註', kind: 'text' },
]

const erpCustomerBackfillFieldAliases: Partial<Record<ErpCustomerBackfillField, string[]>> = {
  erpCustomerCode: ['customer_code', 'erp_customer_code', 'group_code'],
  companyName: ['customer_name', 'company_name', 'group_name'],
  taxId: ['tax_id', 'taxID', 'taxid'],
  industry: ['channel', 'category'],
  companyAddress: ['reg_address', 'ship_address', 'company_address'],
  companyPhone: ['phone2', 'phone1', 'company_phone'],
  contactName: ['contact_person', 'contact_name'],
  contactEmail: ['email', 'contact_email'],
  paymentTerms: ['payment_terms'],
  salesRegion: ['region', 'sales_region'],
}

const fieldDefinitionByKey = new Map(
  erpCustomerBackfillFieldDefinitions.map((definition) => [definition.field, definition]),
)

export function sanitizeErpCustomerBackfillSource(
  source: Record<string, unknown>,
): ErpCustomerBackfillSource {
  const sanitized: ErpCustomerBackfillSource = {}
  for (const definition of erpCustomerBackfillFieldDefinitions) {
    const value = pickErpCustomerBackfillValue(source, definition.field)
    if (isAcceptedErpCustomerBackfillValue(value)) {
      sanitized[definition.field] = value
    }
  }
  return sanitized
}

function pickErpCustomerBackfillValue(
  source: Record<string, unknown>,
  field: ErpCustomerBackfillField,
) {
  if (Object.prototype.hasOwnProperty.call(source, field)) return source[field]

  for (const alias of erpCustomerBackfillFieldAliases[field] ?? []) {
    const value = source[alias]
    if (typeof value === 'string' && value.trim() !== '') return value
    if (typeof value === 'number') return value
  }

  return undefined
}

function isAcceptedErpCustomerBackfillValue(
  value: unknown,
): value is string | number | null | undefined {
  return typeof value === 'string' || typeof value === 'number' || value === null
}

export function buildErpCustomerBackfillComparison(
  customer: ManagedCustomer,
  erpCustomer: ErpCustomerBackfillSource,
): ErpCustomerBackfillFieldComparison[] {
  return erpCustomerBackfillFieldDefinitions.map((definition) => {
    const currentValue = normalizeDisplayValue(customer[definition.field], definition.kind)
    const erpValue = normalizeDisplayValue(erpCustomer[definition.field], definition.kind)
    const state = getComparisonState(currentValue, erpValue)
    return {
      field: definition.field,
      label: definition.label,
      currentValue,
      erpValue,
      state,
      selectable: state === 'changed' || state === 'new',
    }
  })
}

export function buildErpCustomerBackfillPatch(
  rows: ErpCustomerBackfillFieldComparison[],
  selectedFields: ErpCustomerBackfillField[],
): Partial<ManagedCustomer> {
  return buildErpCustomerBackfillPatchWithTargets(rows, selectedFields, {})
}

export function buildErpCustomerBackfillPatchWithTargets(
  rows: ErpCustomerBackfillFieldComparison[],
  selectedFields: ErpCustomerBackfillField[],
  targetOverrides: Partial<Record<ErpCustomerBackfillField, ErpCustomerBackfillField>>,
): Partial<ManagedCustomer> {
  const selected = new Set(selectedFields)
  const patch: Partial<ManagedCustomer> = {}
  for (const row of rows) {
    if (!selected.has(row.field) || !row.selectable) continue
    const targetField = targetOverrides[row.field] ?? row.field
    const definition = fieldDefinitionByKey.get(targetField)
    if (!definition) continue
    ;(patch as Record<ErpCustomerBackfillField, string | number>)[targetField] =
      definition.kind === 'number' ? Number(row.erpValue) || 0 : row.erpValue
  }
  return patch
}

function getComparisonState(
  currentValue: string,
  erpValue: string,
): ErpCustomerBackfillFieldComparison['state'] {
  if (!erpValue) return 'empty'
  if (!currentValue) return 'new'
  return currentValue === erpValue ? 'same' : 'changed'
}

function normalizeDisplayValue(value: unknown, kind: FieldKind) {
  if (value === null || value === undefined) return ''
  if (kind === 'number') {
    const numeric = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
    return Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : ''
  }
  return String(value).trim()
}
